import React, { useEffect, useId, useRef, useState } from 'react';
import {
  trackKeepItProvider,
  trackKeepItSent,
  trackKeepItShown,
  trackKeepItSubmitted,
  trackSignupStep,
} from '@/utils/signupRoad';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getAdditionalUserInfo, type UserCredential } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { Loader2 } from 'lucide-react';
import { auth, functions } from '@/firebase';
import { AppleLogo, GoogleLogo } from '@/shared/components/SocialProviderIcons';
import { AgeConfirm, AGE_REQUIRED_MESSAGE } from '@/shared/components/AgeConfirm';
import { useEnhancedAuth } from '@/contexts/AuthContext';
import type { CaptureLeadRequest, CaptureLeadResponse } from '@/types/backendCallables';
import { ANALYTICS_EVENTS, logAnalyticsEvent } from '@/utils/Analytics';
import { shouldUseRedirect } from '@/utils/browserEnv';
import { callWithTimeout, CallTimeoutError } from '@/utils/callWithTimeout';
import { getAttributionData, getLeadAttribution } from '@/utils/emailAttribution';
import {
  clearPendingSocialSignup,
  loadPendingSocialSignup,
  savePendingSocialSignup,
  type PendingSocialSignup,
} from '@/utils/pendingSocialSignup';
import { completeTrialSignup, TrialProvisioningError } from '@/utils/trialSignup';
import { isPassPlanTrialAtMintEnabled } from '@/config/featureFlags';
import { redeemPassPlanAuth } from '@/utils/passPlanAuth';
import { attachHandoffOnSignup, buildHandoffPayload } from './session';

type Provider = 'google' | 'apple';

export const KEEP_IT_DESTINATION = '/onboarding?src=public_diagnostic';

const CANCEL_CODES = new Set(['auth/popup-closed-by-user', 'auth/cancelled-popup-request', 'auth/user-cancelled']);
const REDIRECT_CODES = new Set([
  'auth/popup-blocked',
  'auth/web-storage-unsupported',
  'auth/operation-not-supported-in-this-environment',
]);
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const codeOf = (err: unknown) => (err as { code?: string })?.code ?? '';

/**
 * One page load finishes Keep-it redirect completion once.
 * Without this, a pathname remount or React StrictMode double-mount can start two
 * effects that both hold the same pending ctx through `await completeSocialRedirect`
 * and both call `completeTrialSignup` (duplicate analytics / handoff claim).
 */
let keepItRedirectFinish: Promise<void> | null = null;

/** Test-only: reset the in-flight guard between cases. */
export function __resetKeepItRedirectFinishForTests(): void {
  keepItRedirectFinish = null;
}

/**
 * Apple/Google on the end screen: sign in, start the trial for a new account,
 * claim the six turns, then onboarding. An existing account gets no new trial.
 */
export function useKeepItSignIn() {
  const { signInWithGoogle, signInWithApple, startSocialRedirect, completeSocialRedirect } = useEnhancedAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [pending, setPending] = useState<Provider | null>(() => loadPendingSocialSignup()?.provider ?? null);
  const [error, setError] = useState<string | null>(null);

  const finish = async (cred: UserCredential, ctx: PendingSocialSignup) => {
    if (getAdditionalUserInfo(cred)?.isNewUser) {
      await completeTrialSignup(cred.user, ctx, ctx.provider);
      trackSignupStep('diagnostic_email_captured', 'captured', false, { method: ctx.provider, diagnostic_variant: 'six_turn' });
    } else {
      await attachHandoffOnSignup(cred.user.uid, buildHandoffPayload());
    }
    navigate(KEEP_IT_DESTINATION);
  };

  const fail = (provider: Provider, err: unknown) => {
    const code = codeOf(err);
    logAnalyticsEvent(ANALYTICS_EVENTS.SIGN_UP_FAILED, {
      signup_method: provider,
      reason: code || (err instanceof Error ? err.message : 'keep-it-failed'),
      timestamp: new Date().toISOString(),
    });
    setPending(null);
    if (CANCEL_CODES.has(code)) return;
    if (err instanceof CallTimeoutError) setError('This is taking longer than expected. Please try again.');
    else if (err instanceof TrialProvisioningError) setError(err.message);
    else setError(`${provider === 'apple' ? 'Apple' : 'Google'} sign-in didn't finish. Try again, or use your email.`);
  };

  const redirect = async (ctx: PendingSocialSignup) => {
    savePendingSocialSignup(ctx);
    await startSocialRedirect(ctx.provider);
  };

  // Mobile sign-in leaves the page; the pending bridge brings the provider back here.
  // Auth handlers often land on `/` — restore /try (or whatever returnPath) first so
  // the URL never mid-flow switches away from the diagnostic door.
  //
  // clearPendingSocialSignup MUST run before finish: deps include pathname, so a
  // navigate from finish would otherwise re-enter this effect with the same ctx
  // and double-call completeTrialSignup. Clear first; a remount finds no ctx.
  // Once we have a Firebase cred, always finish — even if this effect was cleaned
  // up mid-await. Skipping finish after clear left the user signed in with no trial.
  // keepItRedirectFinish stops a second effect (StrictMode / pathname) from also finishing.
  useEffect(() => {
    const ctx = loadPendingSocialSignup();
    if (!ctx) return;
    if (ctx.returnPath === '/try' && pathname !== '/try') {
      navigate('/try', { replace: true });
      return;
    }
    if (keepItRedirectFinish) return;

    let cancelled = false;
    keepItRedirectFinish = (async () => {
      try {
        const cred = await completeSocialRedirect();
        if (!cred?.user) {
          clearPendingSocialSignup();
          keepItRedirectFinish = null;
          if (!cancelled) setPending(null);
          return;
        }
        // Guard: clear before finish so a pathname-driven remount cannot mint twice.
        clearPendingSocialSignup();
        await finish(cred, ctx);
      } catch (err) {
        clearPendingSocialSignup();
        keepItRedirectFinish = null;
        if (!cancelled) fail(ctx.provider, err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pathname, navigate]);

  const start = async (provider: Provider) => {
    trackKeepItProvider(false, provider);
    setError(null);
    setPending(provider);
    const attribution = getAttributionData();
    const ctx: PendingSocialSignup = {
      provider,
      marketingConsent: true,
      userSegment: 'unknown',
      acquisitionChannel: attribution?.acquisitionChannel,
      utmParams: attribution?.utmParams,
      schoolId: attribution?.schoolId,
      returnPath: pathname === '/try' ? '/try' : undefined,
    };
    try {
      if (shouldUseRedirect()) {
        await redirect(ctx);
        return;
      }
      const signIn = provider === 'google' ? signInWithGoogle : signInWithApple;
      const cred = await signIn(
        { marketingEmailConsent: true, transactionalEmailConsent: true, termsAcceptedAt: new Date().toISOString() },
        true
      );
      await finish(cred, ctx);
    } catch (err) {
      if (!REDIRECT_CODES.has(codeOf(err))) {
        fail(provider, err);
        return;
      }
      try {
        await redirect(ctx);
      } catch (redirectErr) {
        fail(provider, redirectErr);
      }
    }
  };

  return { pending, error, start };
}

export type KeepItSignIn = ReturnType<typeof useKeepItSignIn>;

/** captureLead mails the only sign-in link; an older backend without `handoff` rejects it, so retry once without. */
export async function sendKeepItLink(email: string): Promise<{ token?: string; sent: boolean }> {
  const send = (withHandoff: boolean) => {
    const payload: CaptureLeadRequest = {
      email,
      source: 'public_diagnostic',
      ...(withHandoff ? { handoff: buildHandoffPayload() } : {}),
      ...getLeadAttribution(),
    };
    return callWithTimeout(() => httpsCallable(functions, 'captureLead')(payload), {
      timeoutMs: 15000,
      retries: 0,
    });
  };
  let res;
  try {
    res = await send(true);
  } catch (err) {
    if (codeOf(err) !== 'functions/invalid-argument') throw err;
    res = await send(false);
  }
  const data = res?.data as CaptureLeadResponse | undefined;
  // An older backend omits `sent`; only an explicit false means the letter did not go out.
  return { token: data?.downloadToken, sent: data?.sent !== false };
}

const socialButton =
  'w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-display font-semibold transition-colors disabled:opacity-60';

export const KeepItPanel: React.FC<{ signIn: KeepItSignIn; onSent: (email: string) => void }> = ({
  signIn,
  onSent,
}) => {
  const inputId = useId();
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [unsentTo, setUnsentTo] = useState<string | null>(null);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const busy = sending || signIn.pending != null;
  const error = emailError ?? signIn.error;
  const shownTracked = useRef(false);
  useEffect(() => {
    if (shownTracked.current) return;
    shownTracked.current = true;
    trackKeepItShown(false);
    // Keep-it is the six-turn capture screen; the funnel's completed -> capture step reads this name.
    trackSignupStep('diagnostic_capture_viewed', 'capture', false, { diagnostic_variant: 'six_turn' });
  }, []);

  const startSocial = (provider: Provider) => {
    if (!ageConfirmed) {
      setEmailError(AGE_REQUIRED_MESSAGE);
      return;
    }
    setEmailError(null);
    void signIn.start(provider);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ageConfirmed) {
      setEmailError(AGE_REQUIRED_MESSAGE);
      return;
    }
    const address = email.trim();
    if (!EMAIL_PATTERN.test(address)) {
      setEmailError('Enter an email address like you@example.com.');
      return;
    }
    await send(address);
  };

  const send = async (address: string) => {
    setEmailError(null);
    setUnsentTo(null);
    setSending(true);
    trackKeepItSubmitted(false);
    try {
      const { token, sent } = await sendKeepItLink(address);
      // Never throws; a failed mint leaves the emailed link as the way in.
      if (isPassPlanTrialAtMintEnabled()) {
        const { outcome } = await redeemPassPlanAuth(token, 'try_capture', 'six_turn');
        // The later mail-link redeem drops the server copy without claiming into an existing account.
        const uid = auth.currentUser?.uid;
        if (outcome === 'signed-in' && uid) await attachHandoffOnSignup(uid, buildHandoffPayload());
      }
      if (!sent) {
        setEmailError("We couldn't send your sign-in link. Try again.");
        setUnsentTo(address);
        setSending(false);
        return;
      }
      // captureLead never says whether the address has an account.
      trackKeepItSent(false, false);
      trackSignupStep('diagnostic_email_captured', 'captured', false, { method: 'email', diagnostic_variant: 'six_turn' });
      onSent(address);
    } catch {
      setEmailError("We couldn't send the link. Check the address and try again.");
      setSending(false);
    }
  };

  return (
    <div className="space-y-4 border-t-2 border-neutral-200 pt-5" data-testid="keep-it">
      <div className="space-y-1">
        <h3 className="heading-3">Keep it</h3>
        <p className="body-small text-neutral-700">Save your six turns to a free account. No password to make.</p>
      </div>

      <AgeConfirm
        checked={ageConfirmed}
        onChange={(checked) => {
          setAgeConfirmed(checked);
          if (checked && emailError === AGE_REQUIRED_MESSAGE) setEmailError(null);
        }}
      />

      <div className="space-y-3">
        <button
          type="button"
          onClick={() => startSocial('apple')}
          disabled={busy}
          className={`${socialButton} bg-neutral-900 text-white hover:bg-neutral-800 shadow-ledge`}
          data-testid="keep-it-apple"
        >
          {signIn.pending === 'apple' ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : <AppleLogo />}
          Continue with Apple
        </button>
        <button
          type="button"
          onClick={() => startSocial('google')}
          disabled={busy}
          className={`${socialButton} border-2 border-neutral-200 bg-white text-neutral-900 hover:bg-neutral-50 shadow-ledge`}
          data-testid="keep-it-google"
        >
          {signIn.pending === 'google' ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : <GoogleLogo />}
          Continue with Google
        </button>
      </div>

      <div className="flex items-center gap-3 text-sm text-neutral-500" aria-hidden="true">
        <span className="h-px flex-1 bg-neutral-200" />
        or
        <span className="h-px flex-1 bg-neutral-200" />
      </div>

      <form onSubmit={(e) => void submit(e)} noValidate className="space-y-3">
        <label htmlFor={inputId} className="sr-only">
          Email address
        </label>
        <input
          id={inputId}
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-invalid={emailError ? true : undefined}
          className="w-full rounded-xl border-2 border-neutral-200 bg-neutral-50 px-4 py-3 text-neutral-800 focus:border-primary-400 focus:outline-none"
          data-testid="keep-it-email"
        />
        <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="keep-it-send">
          {sending ? 'Sending…' : 'Email me a sign-in link'}
        </button>
      </form>

      <p role="alert" aria-live="polite" className="text-sm font-semibold text-red-700" data-testid="keep-it-error">
        {error}
      </p>
      {unsentTo && (
        <button
          type="button"
          onClick={() => void send(unsentTo)}
          disabled={busy}
          className="btn-secondary w-full"
          data-testid="keep-it-retry"
        >
          Try again
        </button>
      )}

      <div className="space-y-1">
        <p className="caption">New here? Saving starts your free 7-day trial. No card needed.</p>
        <p className="caption" data-testid="keep-it-terms">
          By continuing, you agree to our{' '}
          <Link to="/terms" className="text-primary-600 hover:underline">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link to="/privacy" className="text-primary-600 hover:underline">
            Privacy Policy
          </Link>
          , and to get study emails from Pam. Unsubscribe anytime.
        </p>
      </div>
    </div>
  );
};

export const CheckInbox: React.FC<{ email: string; onDifferentEmail: () => void }> = ({ email, onDifferentEmail }) => {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => headingRef.current?.focus(), []);

  return (
    <div className="card space-y-4" data-testid="keep-it-sent">
      <h2 ref={headingRef} tabIndex={-1} className="heading-2 focus:outline-none">
        Check your inbox
      </h2>
      <p className="body-base">We sent a sign-in link to {email}.</p>
      <p className="body-small text-neutral-800">
        Tap the button in that email to save your six turns. If you are new, it also starts your free 7-day trial. The link is just for you.
      </p>
      <p className="caption">Nothing there in a few minutes? Check spam or promotions.</p>
      <button type="button" onClick={onDifferentEmail} className="btn-secondary w-full" data-testid="keep-it-different-email">
        Use a different email
      </button>
    </div>
  );
};
